(() => {
  if (window.laceupCartLoaded) return;
  window.laceupCartLoaded = true;

  const routesRoot = () => window.Shopify?.routes?.root || '/';
  const cartDrawer = () => document.querySelector('cart-drawer');
  const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));
  let busy = false;
  let giftEnforcementBusy = false;
  let giftEnforcementTimer = null;

  function normalizeDrawerState(drawer = cartDrawer()) {
    if (!drawer) return;
    const hasItems = Boolean(drawer.querySelector('.cart-item'));
    if (!hasItems) return;

    drawer.classList.remove('is-empty');
    drawer.querySelector('.drawer__inner')?.classList.remove('is-empty');
    drawer.querySelector('cart-drawer-items')?.classList.remove('is-empty');
  }

  function hardenDrawerRender(drawer = cartDrawer()) {
    if (!drawer || drawer.dataset.laceupRenderHardened === 'true') return;
    const originalRender = typeof drawer.renderContents === 'function'
      ? drawer.renderContents.bind(drawer)
      : null;
    if (!originalRender) return;

    drawer.dataset.laceupRenderHardened = 'true';
    drawer.renderContents = (response) => {
      if (Number(response?.item_count) > 0 || response?.items?.length) {
        drawer.classList.remove('is-empty');
      }
      const result = originalRender(response);
      normalizeDrawerState(drawer);
      requestAnimationFrame(() => normalizeDrawerState(drawer));
      setTimeout(() => normalizeDrawerState(drawer), 0);
      return result;
    };

    normalizeDrawerState(drawer);
  }

  function countdownDeadline() {
    const key = 'laceup_cart_deadline';
    const stored = Number(sessionStorage.getItem(key));
    if (stored > Date.now()) return stored;
    const deadline = Date.now() + (10 * 60 * 1000);
    sessionStorage.setItem(key, String(deadline));
    return deadline;
  }

  function updateCountdown() {
    let remaining = Math.max(0, countdownDeadline() - Date.now());
    if (remaining === 0) {
      sessionStorage.removeItem('laceup_cart_deadline');
      remaining = countdownDeadline() - Date.now();
    }
    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    document.querySelectorAll('[data-laceup-countdown]').forEach((element) => {
      element.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    });
  }

  async function jsonRequest(url, options = {}) {
    for (let attempt = 1; attempt <= 5; attempt += 1) {
      const response = await fetch(url, options);
      const body = await response.json().catch(() => null);
      if (response.ok && body) return body;
      if (attempt < 5) await wait(500 * attempt);
      else throw new Error(body?.description || body?.message || `Shopify request failed (${response.status})`);
    }
  }

  function sectionFormData(variantId, propertyName, propertyValue) {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    if (propertyName && propertyValue) formData.append(`properties[${propertyName}]`, propertyValue);
    const drawer = cartDrawer();
    if (drawer?.getSectionsToRender) {
      formData.append('sections', drawer.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', location.pathname);
    }
    return formData;
  }

  async function renderCart(response) {
    if (/\/cart\/?$/.test(location.pathname)) {
      location.reload();
      return;
    }
    const drawer = cartDrawer();
    if (drawer?.renderContents && response.sections) {
      drawer.classList.remove('is-empty');
      drawer.renderContents(response);
      normalizeDrawerState(drawer);
      scheduleGiftEnforcement();
      return;
    }
    location.assign(`${routesRoot()}cart`);
  }

  // De gratis boxershorts worden NIET meer automatisch verwijderd onder € 100.
  // Shopify's automatische korting bepaalt de prijs: vanaf € 100 gratis, daaronder € 29,95.
  // Alleen een tweede (dubbele) cadeau-regel wordt nog opgeschoond.
  async function enforceGiftThreshold() {
    if (giftEnforcementBusy) return;
    giftEnforcementBusy = true;
    try {
      const cart = await jsonRequest(`${routesRoot()}cart.js`, {headers: {Accept: 'application/json'}});
      const isGiftItem = (item) => item.properties?._laceup_gift === 'free-100';
      const giftItems = cart.items.filter(isGiftItem);
      if (giftItems.length < 2) return;
      const drawer = cartDrawer();
      const keptGiftKey = giftItems[0].key;
      for (const item of giftItems) {
        const allowedQuantity = item.key === keptGiftKey ? 1 : 0;
        if (item.quantity === allowedQuantity) continue;
        const sections = drawer?.getSectionsToRender?.().map((section) => section.id) || [];
        const response = await jsonRequest(`${routesRoot()}cart/change.js`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({id: item.key, quantity: allowedQuantity, sections, sections_url: location.pathname}),
        });
        await renderCart(response);
      }
    } catch {
      // Shopify's cart remains authoritative; retry after the next cart render.
    } finally {
      giftEnforcementBusy = false;
    }
  }

  function scheduleGiftEnforcement() {
    clearTimeout(giftEnforcementTimer);
    giftEnforcementTimer = setTimeout(enforceGiftThreshold, 450);
  }

  function closeCartDrawer(drawer) {
    if (!drawer) return;
    if (typeof drawer.close === 'function') {
      drawer.close();
      return;
    }
    drawer.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
  }

  document.addEventListener('pointerup', (event) => {
    const closeButton = event.target.closest?.('cart-drawer .drawer__close');
    if (!closeButton) return;
    event.preventDefault();
    event.stopPropagation();
    closeCartDrawer(closeButton.closest('cart-drawer'));
  }, { capture: true });

  function money(amount, currency = 'EUR') {
    return new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', {
      style: 'currency',
      currency,
    }).format(Number(amount || 0) / 100);
  }

  function recommendationCard(product, currency) {
    const availableVariants = (product.variants || []).filter((variant) => variant.available);
    const firstVariant = availableVariants[0];
    if (!firstVariant) return null;
    const article = document.createElement('article');
    article.className = 'laceup-cart__recommendation';

    const image = document.createElement('img');
    image.loading = 'lazy';
    image.width = 110;
    image.height = 110;
    image.alt = product.title || '';
    image.src = product.featured_image || product.images?.[0] || '';

    const content = document.createElement('div');
    const title = document.createElement('strong');
    title.textContent = product.title || '';
    const price = document.createElement('div');
    price.className = 'laceup-cart__offer-price';
    const priceText = document.createElement('b');
    priceText.textContent = money(firstVariant.price, currency);
    price.append(priceText);
    content.append(title, price);

    if (availableVariants.length > 1) {
      const select = document.createElement('select');
      select.dataset.laceupVariantSelect = '';
      select.setAttribute('aria-label', `Kies variant voor ${product.title}`);
      availableVariants.forEach((variant) => {
        const option = document.createElement('option');
        option.value = variant.id;
        option.textContent = variant.title;
        select.append(option);
      });
      content.append(select);
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'laceup-cart__add';
    button.dataset.laceupCartAdd = '';
    button.dataset.variantId = firstVariant.id;
    button.textContent = 'Toevoegen';
    content.append(button);
    article.append(image, content);
    return article;
  }

  async function loadRecommendations() {
    const sections = [...document.querySelectorAll('[data-laceup-recommendations]')];
    if (!sections.length) return;
    const cart = await jsonRequest(`${routesRoot()}cart.js`, {headers: {Accept: 'application/json'}}).catch(() => null);
    const cartProductIds = new Set((cart?.items || []).map((item) => Number(item.product_id)));

    for (const section of sections) {
      const productId = Number(section.dataset.productId);
      const list = section.querySelector('[data-laceup-recommendation-list]');
      if (!productId || !list || section.dataset.laceupRecommendationsState) continue;
      section.dataset.laceupRecommendationsState = 'loading';
      try {
        const url = `${routesRoot()}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=8&intent=related`;
        const data = await jsonRequest(url, {headers: {Accept: 'application/json'}});
        const products = (data.products || [])
          .filter((product) => !cartProductIds.has(Number(product.id)))
          .slice(0, 3);
        list.replaceChildren(...products.map((product) => recommendationCard(product, cart?.currency)).filter(Boolean));
        section.hidden = list.children.length === 0;
        section.dataset.laceupRecommendationsState = 'complete';
      } catch {
        section.hidden = true;
        section.dataset.laceupRecommendationsState = 'error';
      }
    }
  }

  async function addFromButton(button) {
    if (busy || button.disabled) return;
    const container = button.closest('.laceup-cart__offer-card, .laceup-cart__recommendation, .laceup-cart__insurance');
    const variantId = container?.querySelector('[data-laceup-variant-select]')?.value || button.dataset.variantId;
    if (!variantId) return;
    busy = true;
    button.disabled = true;
    const oldText = button.textContent;
    button.textContent = 'Bezig…';
    try {
      const response = await jsonRequest(`${routesRoot()}cart/add.js`, {
        method: 'POST',
        headers: {'X-Requested-With': 'XMLHttpRequest'},
        body: sectionFormData(variantId, button.dataset.propertyName, button.dataset.propertyValue),
      });
      await renderCart(response);
      window.setTimeout(loadRecommendations, 300);
    } catch (error) {
      button.textContent = error.message;
      await wait(2200);
      button.textContent = oldText;
      button.disabled = false;
    } finally {
      busy = false;
    }
  }

  document.addEventListener('click', (event) => {
    const closeButton = event.target.closest?.('cart-drawer .drawer__close');
    if (closeButton) {
      event.preventDefault();
      closeCartDrawer(closeButton.closest('cart-drawer'));
      return;
    }
    const button = event.target.closest('[data-laceup-cart-add]');
    if (!button) return;
    event.preventDefault();
    addFromButton(button);
  });

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-laceup-variant-select]');
    if (!select) return;
    const container = select.closest('.laceup-cart__offer-card, .laceup-cart__recommendation');
    const button = container?.querySelector('[data-laceup-cart-add]');
    if (button) button.dataset.variantId = select.value;
  });

  updateCountdown();
  setInterval(updateCountdown, 1000);
  scheduleGiftEnforcement();
  loadRecommendations();
  const observedCart = cartDrawer();
  if (observedCart) {
    hardenDrawerRender(observedCart);
    window.customElements?.whenDefined('cart-drawer').then(() => hardenDrawerRender(observedCart));
    new MutationObserver(() => {
      normalizeDrawerState(observedCart);
      scheduleGiftEnforcement();
      loadRecommendations();
    }).observe(observedCart, {subtree: true, childList: true});
  }
})();
